import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Scissors, User, MapPin, Award, Sun, Moon, Menu, X, Heart, Trophy, Mail, LogOut, LogIn } from "lucide-react";
import { buttonVariants, Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTheme } from "@/src/lib/useTheme";
import { useAuth } from "@/src/lib/useAuth";
import { motion, AnimatePresence } from "motion/react";

export function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const { user, openAuthModal, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav className="fixed top-0 inset-x-0 z-50 glass border-b border-neutral-200 dark:border-white/5 h-16 px-6 flex items-center justify-between">
        <Link to="/" className="flex flex-col group" onClick={closeMobile}>
          <span className="text-[22px] sm:text-2xl font-light tracking-wide leading-tight">
            p<span className="text-emerald-500">α</span>rb<span className="text-emerald-500">ε</span>rkon
          </span>
          <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-neutral-400 dark:text-white/40">
            cyprus barbershop directory
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6 text-sm font-medium text-neutral-600 dark:text-white/70">
          <Link to="/cities/limassol" className="hover:text-neutral-900 dark:hover:text-white transition-colors flex items-center gap-1">
            <MapPin className="w-4 h-4" /> Cities
          </Link>
          <Link to="/leaderboard" className="hover:text-neutral-900 dark:hover:text-white transition-colors flex items-center gap-1">
            <Award className="w-4 h-4" /> Leaderboard
          </Link>
          <Link to="/contact" className="hover:text-neutral-900 dark:hover:text-white transition-colors flex items-center gap-1">
            <Mail className="w-4 h-4" /> Contact
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-neutral-200 dark:hover:bg-white/10 transition-colors"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
          </button>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/profile" className={cn(buttonVariants({ variant: "default" }), "bg-emerald-600 hover:bg-emerald-500 text-white rounded-full px-5 cursor-pointer")}>
                <User className="w-4 h-4 mr-2" /> {user.name.split(" ")[0]}
              </Link>
              <button
                onClick={signOut}
                title="Sign Out"
                aria-label="Sign out of your account"
                className="p-2 rounded-full hover:bg-red-500/10 text-neutral-400 hover:text-red-400 transition-colors"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => openAuthModal("login")}
                className="text-xs font-semibold px-4 py-2 rounded-full hover:bg-white/10 text-neutral-300 transition-colors flex items-center gap-1.5"
              >
                <LogIn className="w-3.5 h-3.5" /> Sign In
              </button>
              <Button
                onClick={() => openAuthModal("register")}
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-xs rounded-full px-4 h-9 shadow-sm"
              >
                Register
              </Button>
            </div>
          )}

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-full hover:bg-neutral-200 dark:hover:bg-white/10 transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 top-16 bottom-0 z-40 glass border-t border-neutral-200 dark:border-white/5 md:hidden overflow-y-auto"
          >
            <div className="px-6 py-6 space-y-2">
              <Link to="/" onClick={closeMobile} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-neutral-100 dark:hover:bg-white/5 transition-colors text-neutral-700 dark:text-white/80 font-medium">
                <Scissors className="w-5 h-5 text-emerald-500" /> Home
              </Link>
              
              <div className="px-4 pt-4 pb-2 text-xs font-semibold text-neutral-400 dark:text-white/40 uppercase tracking-wider">Cities</div>
              {["Limassol", "Nicosia", "Larnaca", "Paphos", "Famagusta", "Kyrenia"].map(city => (
                <Link key={city} to={`/cities/${city.toLowerCase()}`} onClick={closeMobile} className="flex items-center gap-3 px-4 py-2.5 pl-8 rounded-xl hover:bg-neutral-100 dark:hover:bg-white/5 transition-colors text-neutral-600 dark:text-white/70">
                  <MapPin className="w-4 h-4 text-emerald-500/60" /> {city}
                </Link>
              ))}

              <div className="border-t border-neutral-200 dark:border-white/10 my-3" />

              <Link to="/leaderboard" onClick={closeMobile} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-neutral-100 dark:hover:bg-white/5 transition-colors text-neutral-700 dark:text-white/80 font-medium">
                <Trophy className="w-5 h-5 text-amber-400" /> Leaderboard
              </Link>
              <Link to="/contact" onClick={closeMobile} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-neutral-100 dark:hover:bg-white/5 transition-colors text-neutral-700 dark:text-white/80 font-medium">
                <Mail className="w-5 h-5 text-blue-400" /> Contact
              </Link>
              <Link to="/profile" onClick={closeMobile} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-neutral-100 dark:hover:bg-white/5 transition-colors text-neutral-700 dark:text-white/80 font-medium">
                <User className="w-5 h-5 text-purple-400" /> My Profile
              </Link>
              <Link to="/favorites" onClick={closeMobile} className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-neutral-100 dark:hover:bg-white/5 transition-colors text-neutral-700 dark:text-white/80 font-medium">
                <Heart className="w-5 h-5 text-red-400" /> Favorites
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
